import Collection from '@model-core/core/Collection';
import { PersonCombobox, SEARCH_RESULTS, type PersonSearchResult } from '@/components/form/PersonCombobox';
import MemberModel from '@/models/entities/MemberModel';
import UserModel from '@/models/entities/UserModel';
import { listPage } from '@/models/listPage';

/** Utilisateurs correspondant à la saisie (10, triés par NOM puis prénom), ceux déjà employés de la société étant grisés */
async function searchUsers(companyId: number, query: string): Promise<PersonSearchResult<UserModel>> {
  const { models: users, totalItems } = await listPage(UserModel, {
    filters: { search: query },
    order: { lastName: 'asc', firstName: 'asc' },
    page: 1,
    itemsPerPage: SEARCH_RESULTS,
  });
  const members = users.length
    ? (
        await new Collection(MemberModel)
          .setFilter('company', String(companyId))
          .setFilter('user', users.map((user) => user.id) as never)
          .list()
      ).collection.models
    : [];

  return {
    items: users,
    totalItems,
    disabledReasons: new Map(members.map((member) => [String(member.userId), 'déjà employé'])),
  };
}

/**
 * Choix d'un utilisateur à ajouter à une société (PersonCombobox) : les déjà employés sont grisés ; le bouton + crée
 * un utilisateur (seulement si onCreate est fourni : créer un utilisateur est réservé à l'admin).
 */
export function UserCombobox({
  id,
  companyId,
  value,
  onChange,
  onCreate,
}: {
  id: string;
  companyId: number;
  value: UserModel | null;
  onChange: (user: UserModel | null) => void;
  /** Bouton + : création d'un utilisateur (formulaire vide) ; absent, pas de bouton */
  onCreate?: () => void;
}) {
  return (
    <PersonCombobox
      id={id}
      label="Utilisateurs"
      value={value}
      onChange={onChange}
      search={(query) => searchUsers(companyId, query)}
      searchKey={String(companyId)}
      onCreate={onCreate}
      createLabel="Créer un nouvel utilisateur"
      noResultCreateText="Aucun utilisateur ne correspond : créez-le avec"
      noResultText="Aucun utilisateur ne correspond : un administrateur doit d’abord le créer."
    />
  );
}
